import { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import logger from '@/utils/logger';
import { ConnectionManager, ConnectionState, ConnectionParams, ConnectionResult } from '@/lib/core';
import { useJoin, useLeave } from '@/lib/useCommon';

/**
 * useConnectionManager Hook - 在React组件中使用ConnectionManager
 */
// 模块级单例，保证全局仅一个 ConnectionManager 实例
let sharedManager: ConnectionManager | null = null;

export const useConnectionManager = (config?: any) => {
  const connectionManagerRef = useRef<ConnectionManager | null>(null);
  const [state, setState] = useState<ConnectionState>(ConnectionState.IDLE);
  const [isReady, setIsReady] = useState(false);

  // 使用原有的hooks
  const [joining, dispatchJoin] = useJoin();
  const dispatchLeave = useLeave();

  // 监听redux状态
  const isJoined = useSelector((state: any) => state.room.isJoined);

  // 初始化ConnectionManager（组件不再销毁实例，交由上层统一管理）
  useEffect(() => {
    if (connectionManagerRef.current) return;

    if (!sharedManager) {
      sharedManager = new ConnectionManager(config);
    }

    connectionManagerRef.current = sharedManager;

    const unsubscribe = connectionManagerRef.current.onStateChange((event) => {
      setState(event.to);
    });

    setIsReady(true);

    return () => {
      unsubscribe();
      // 组件卸载不再销毁管理器，避免并发操作回调推进到已销毁实例
    };
  }, [config]);

  // 同步redux状态到ConnectionManager（仅在合法转换路径下推进状态）
  useEffect(() => {
    const mgr = connectionManagerRef.current;
    if (!mgr || !isReady) return;

    // redux 显示已加入：只允许在 CONNECTING -> CONNECTED 时推进
    if (isJoined) {
      if (state === ConnectionState.CONNECTING) {
        logger.debug('[useConnectionManager] redux已连接，推进 CONNECTING -> CONNECTED');
        mgr.transitionState(ConnectionState.CONNECTED);
      }
      // 其他状态下不强推，避免 idle -> connected 等非法转换
      return;
    }

    // redux 显示未加入：只允许在 DISCONNECTING/CONNECTED -> IDLE 时回落
    if (!isJoined) {
      if (state === ConnectionState.DISCONNECTING || state === ConnectionState.CONNECTED) {
        logger.debug('[useConnectionManager] redux未连接，回落到 IDLE');
        mgr.transitionState(ConnectionState.IDLE);
      }
    }
  }, [isJoined, state, isReady]);

  // 连接方法 - 统一处理所有连接逻辑
  const connect = useCallback(
    async (params: ConnectionParams = {}): Promise<ConnectionResult> => {
      if (!connectionManagerRef.current) {
        throw new Error('ConnectionManager 未初始化');
      }

      // 如果已经在连接中或已连接，直接返回
      if (state === ConnectionState.CONNECTING || state === ConnectionState.CONNECTED || isJoined) {
        logger.info('[useConnectionManager] 已在连接中或已连接，跳过重复连接');
        return {
          success: true,
          state:
            state === ConnectionState.CONNECTED
              ? ConnectionState.CONNECTED
              : ConnectionState.CONNECTING,
          duration: 0,
        };
      }

      try {
        // 先推进到 CONNECTING，确保合法路径（IDLE/ERROR -> CONNECTING）
        connectionManagerRef.current.transitionState(ConnectionState.CONNECTING, { params });

        // 使用操作防护执行连接
        const result = await connectionManagerRef.current.executeOperation(
          'react-connect',
          async () => {
            logger.info('[useConnectionManager] 开始执行连接逻辑', params);

            // 调用原有的连接逻辑
            const joinResult = await dispatchJoin();
            if (joinResult === false) {
              throw new Error('连接失败');
            }

            // 处理DirectRoom的特殊逻辑
            if (params.directRoom) {
              logger.info('[useConnectionManager] DirectRoom模式连接成功');
            }

            return { success: true };
          },
          1000 // 防抖
        );

        if (result.success) {
          // 更新状态到已连接（CONNECTING -> CONNECTED），若已是 CONNECTED 则跳过
          if (connectionManagerRef.current.getState() !== ConnectionState.CONNECTED) {
            connectionManagerRef.current.transitionState(ConnectionState.CONNECTED, { params });
          }
          return {
            success: true,
            state: ConnectionState.CONNECTED,
            duration: result.executionTime,
          };
        }
        throw result.error || new Error('连接被阻止');
      } catch (error) {
        // 更新状态到错误
        connectionManagerRef.current.transitionState(ConnectionState.ERROR, { error });
        return {
          success: false,
          state: ConnectionState.ERROR,
          error: error as Error,
          duration: 0,
        };
      }
    },
    [dispatchJoin, state, isJoined]
  );

  // 断开连接方法 - 统一处理所有断开逻辑
  const disconnect = useCallback(async (): Promise<ConnectionResult> => {
    if (!connectionManagerRef.current) {
      throw new Error('ConnectionManager 未初始化');
    }

    try {
      // 先推进到 DISCONNECTING，确保合法路径（CONNECTED -> DISCONNECTING）
      connectionManagerRef.current.transitionState(ConnectionState.DISCONNECTING);

      // 使用操作防护执行断开连接
      const result = await connectionManagerRef.current.executeOperation(
        'react-disconnect',
        async () => {
          logger.info('[useConnectionManager] 开始执行断开连接逻辑');

          // 调用原有的离开逻辑
          await dispatchLeave();
          return { success: true };
        }
      );

      if (result.success) {
        // 更新状态到空闲（DISCONNECTING -> IDLE），若已是 IDLE 则跳过
        if (connectionManagerRef.current.getState() !== ConnectionState.IDLE) {
          connectionManagerRef.current.transitionState(ConnectionState.IDLE);
        }
        return {
          success: true,
          state: ConnectionState.IDLE,
          duration: result.executionTime,
        };
      }
      throw result.error || new Error('断开连接被阻止');
    } catch (error) {
      // 更新状态到错误
      connectionManagerRef.current.transitionState(ConnectionState.ERROR, { error });
      return {
        success: false,
        state: ConnectionState.ERROR,
        error: error as Error,
        duration: 0,
      };
    }
  }, [dispatchLeave]);

  // 获取统计信息
  const getStats = useCallback(() => {
    return connectionManagerRef.current?.getStats() || null;
  }, []);

  // 强制重置
  const forceReset = useCallback(() => {
    connectionManagerRef.current?.forceReset();
  }, []);

  // 检查各种状态的便捷方法
  const isConnecting = state === ConnectionState.CONNECTING || joining;
  const isConnected = state === ConnectionState.CONNECTED || isJoined;
  const isDisconnecting = state === ConnectionState.DISCONNECTING;
  const hasError = state === ConnectionState.ERROR;

  return {
    // 状态
    state,
    isReady,
    isConnecting,
    isConnected,
    isDisconnecting,
    hasError,

    // 方法
    connect,
    disconnect,

    // 工具方法
    getStats,
    forceReset,

    // 原有的joining状态，保持兼容性
    joining,

    // 管理器实例（仅用于调试）
    manager: connectionManagerRef.current,
  };
};
